// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@eisinc/App/Store/Reducers";
import Tooltip, { TooltipPresentationProps } from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import IconPaginationLargeRight from "@eisinc/XDL/Icons/IconPaginationLargeRight";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, hideElementOnPrint } from "@eisinc/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import mixpanel from "mixpanel-browser";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
        /**
         * XNG-Changes
         * Max number of Links on Tablet that should be available to overflow
         */
        maxNoOfLinksOnSmallScreens?: number;
        tooltip?: TooltipPresentationProps;
    }
>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

// export const Slash = styled(Typography as any).attrs({ children: "/" })`
//     margin: 0 10px 5px;
//     display: block;
// `;
//
export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${hideElementOnPrint}
    ${injectCss}
`;

// <XNG-Changes>
type props = {
    hideCurrentPage?: boolean;
} & BreadcrumbsProps;
// </XNG-Changes>

const Breadcrumbs: React.FC<props> = withTheme(
    ({ links, mergeCss, maxNoOfLinksOnSmallScreens = 2, hideCurrentPage, ...otherProps }) => {
        const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
        const typographyProps = spreadProps("typographyProps");
        const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;

        // XNG-Changes
        const [isActive, setActive] = React.useState(false);
        const { isTabletScreen, customerType } = useAppSelector(state => {
            return {
                isTabletScreen: (state as CustomAppState).xCommon?.isTabletScreen,
                customerType: getSession(state).properties?.["mpExistingCustomer"],
            };
        });

        // <XNG-Changes>
        const handleMpEvent = (link: any) => {
            // eslint-disable-next-line no-prototype-builtins
            if ((window as any).hasOwnProperty("mixpanelInitialized")) {
                mixpanel.track("Breadcrumbs", {
                    "Origin URL": window.location.pathname,
                    "Destination URL": link.href,
                    customer_type: customerType || "new",
                });
            }
        };
        // </XNG-Changes>

        // return null if `links` is an empty object, collection, map, or set.
        if (!links || links.length === 0) {
            return null;
        }

        // <XNG-Changes> Remove last item out of the breadcrumb links if hideCurrentPage is true
        if (hideCurrentPage) {
            links = [...links];
            links.pop();
        }
        // </XNG-Changes>

        const children = links.map((link: any, index: number) => {
            const children = link.children;
            // yield null if no children
            if (!React.Children.count(children)) {
                return null;
            }
            const key = typeof children === "string" ? `${children}_${index}` : index;
            if (index === links.length - 1 && !hideCurrentPage) {
                return (
                    <Typography
                        mergeCss={resolvedMergeCss}
                        {...typographyProps}
                        aria-current="page"
                        key={key}
                        data-test-selector={`breadCrumb_${children}`}
                        // XNG-Changes
                        weight={fontConfig.weight.medium}
                    >
                        {/* XNG-Changes * */}
                        {typeof children === "string" ? translate(children) : children}
                    </Typography>
                );
            }
            return (
                <React.Fragment key={key}>
                    {/* XNG-Changes */}
                    <Link
                        mergeCss={true}
                        typographyProps={{
                            ...typographyProps,
                            weight: index === links.length - 1 ? fontConfig.weight.medium : fontConfig.weight.regular,
                        }}
                        color="text.main"
                        {...link}
                        css={css`
                            &:focus {
                                outline: none;
                            }
                            &:hover > ${TypographyStyle} {
                                color: ${color("text.link")};
                            }
                        `}
                        onClick={() => handleMpEvent(link)}
                    />
                    {!(index === links.length - 1) && <Icon width={8} height={8} src={IconPaginationLargeRight} />}
                </React.Fragment>
            );
        });
        // return null if `links` map yields an array of nulls
        if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
            return null;
        }

        // XNG-Changes
        const maxNoOfLinks = isTabletScreen && maxNoOfLinksOnSmallScreens;
        const tooltipContent = maxNoOfLinks
            ? !isActive && children.length >= maxNoOfLinks && children.slice(1, children.length - 1)
            : null;
        if (tooltipContent && tooltipContent.length > 0) {
            return (
                <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
                    {children[0]}
                    <Tooltip
                        text={children.slice(1, children.length - 1)}
                        triggerComponent={
                            <Clickable
                                data-test-selector="breadcrumbs_menu"
                                aria-label="ellipsis"
                                onClick={() => setActive(true)}
                            >
                                <Typography {...typographyProps} aria-current="page" key="otherBreadcrumbs">
                                    ...
                                </Typography>
                            </Clickable>
                        }
                        {...spreadProps("tooltip")}
                    />
                    <Icon width={8} height={8} src={IconPaginationLargeRight} />
                    {children[children.length - 1]}
                </StyledNav>
            );
        }

        return (
            <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
                {children}
            </StyledNav>
        );
    },
);

Breadcrumbs.displayName = "Breadcrumbs";

/** @component */
export default Breadcrumbs;
